import {
  GetIssueResponseDto,
  MinimalUserDto,
  SaveAttachmentDto,
} from '@issuemanager/api-client';
import { ControlledFileUploader } from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useAllQueries,
  useMonitorApproveIssueLoad,
  useMonitorApproveIssueSubmit,
} from '@issuemanager/apps/client/hooks';
import { useApproveIssue } from '@issuemanager/client/features/issues';
import { useUsers } from '@issuemanager/client/features/users';
import { IssuePermissionsEnum } from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LayoutPanel } from '../layout';

type FormValues = {
  attachments: (SaveAttachmentDto & {
    addedBy: MinimalUserDto;
    createdAt: string;
  })[];
};

type Props = {
  onClose: () => void;
  isOpen: boolean;
  code: GetIssueResponseDto['code'];
};

export function IssueApprovePanel(props: Props) {
  const { isOpen, onClose, code } = props;
  const { t } = useTranslation('issue');
  const { enqueueSnackbar } = useSnackbar();

  const monitorApproveIssueLoad = useMonitorApproveIssueLoad();
  const { isLoading: isUploadAttachmentsLoading } = useAttachmentsContext();
  const users = useUsers({ enabled: isOpen, handleError: true });

  useAllQueries({
    queries: [users],
    onStart: () => {
      monitorApproveIssueLoad.start();
    },
    onSuccess: () => {
      monitorApproveIssueLoad.stop();
    },
    onError: () => {
      monitorApproveIssueLoad.error();
    },
  });

  const approveIssue = useApproveIssue(code, () =>
    enqueueSnackbar(t('manage.approve.success'), {
      variant: 'success',
    }),
  );

  const monitorApproveIssueSubmit = useMonitorApproveIssueSubmit();

  useAllMutations({
    mutations: [approveIssue],
    onStart: () => {
      monitorApproveIssueSubmit.start();
    },
    onSuccess: () => {
      monitorApproveIssueSubmit.stop();
    },
    onError: () => {
      monitorApproveIssueSubmit.error();
    },
  });

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      attachments: [],
    },
  });

  const actionButtonProps = {
    disabled: approveIssue.isPending || isUploadAttachmentsLoading,
    'data-testid': 'approve-footer-button',
  };

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  const onSubmit = async ({ attachments }: FormValues) => {
    await approveIssue.mutateAsync({
      attachments: attachments || [],
    });
    onClose();
  };

  return (
    <LayoutPanel
      isOpen={isOpen}
      onCancel={() => onClose()}
      title={t('manage.approve.title')}
      description={t('manage.approve.description', { issue: code })}
      actionButton={actionButtonProps}
      actionButtonText={
        approveIssue.isPending
          ? t('manage.approve.approving')
          : t('manage.approve.accept')
      }
      isLoading={approveIssue.isPending}
      onSend={handleSubmit}
      content={
        <FormProvider {...form}>
          <ControlledFileUploader
            control={form.control}
            name='attachments'
            disabled={approveIssue.isPending}
            permissions={[IssuePermissionsEnum.IsApprover]}
          />
        </FormProvider>
      }
    />
  );
}
